import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';

import {
    QuizDraft,
    QuizEditorStoreState,
    QuizEditorView,
    saveAnswerStatusPayload,
    saveAnswerValuePayload,
    saveQuestionTitlePayload,
} from './typings';
import { getEmptyQuizQuestion } from './utils';

const initialState: QuizEditorStoreState = {
    sendStatus: LOADING_STATUSES.UNSENT,
    quizDraft: {} as QuizDraft,
    currentQuestionIndex: 0,
    quizLoadingStatus: LOADING_STATUSES.UNSENT,
    isQuizDraftStarted: false,
};

const slice = createSlice({
    name: 'QUIZ_EDITOR',
    initialState: initialState,
    reducers: {
        createQuiz: (state: QuizEditorStoreState) => {
            state.sendStatus = LOADING_STATUSES.LOADING;
        },
        updateQuiz: (state: QuizEditorStoreState) => {
            state.sendStatus = LOADING_STATUSES.LOADING;
        },
        setQuizSentStatus: (state: QuizEditorStoreState, action: PayloadAction<LOADING_STATUSES>) => {
            state.sendStatus = action.payload;
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        getQuiz: (state: QuizEditorStoreState, action: PayloadAction<string>) => {
            state.quizLoadingStatus = LOADING_STATUSES.LOADING;
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        getQuizByLessonId: (state: QuizEditorStoreState, action: PayloadAction<string>) => {
            state.quizLoadingStatus = LOADING_STATUSES.LOADING;
        },
        setQuizDraft: (state: QuizEditorStoreState, action: PayloadAction<QuizDraft>) => {
            state.quizDraft = action.payload;
            state.quizLoadingStatus = LOADING_STATUSES.SUCCESS;
            state.currentQuestionIndex = 0;
        },
        setQuestionTitle: (state: QuizEditorStoreState, action: PayloadAction<saveQuestionTitlePayload>) => {
            const { questionIndex, value } = action.payload;
            state.quizDraft.questions[questionIndex].title = value;
        },
        setCurrentQuestionIndex: (state: QuizEditorStoreState, action: PayloadAction<number>) => {
            state.currentQuestionIndex = action.payload;
        },
        setAnswerStatus: (state: QuizEditorStoreState, action: PayloadAction<saveAnswerStatusPayload>) => {
            const { questionIndex, answerIndex, status } = action.payload;

            for (const answer of state.quizDraft.questions[questionIndex].answers) {
                answer.correct = false;
            }

            state.quizDraft.questions[questionIndex].answers[answerIndex].correct = status;
        },
        setAnswerValue: (state: QuizEditorStoreState, action: PayloadAction<saveAnswerValuePayload>) => {
            const { questionIndex, answerIndex, value } = action.payload;

            state.quizDraft.questions[questionIndex].answers[answerIndex].value = value;
        },
        addSlide: (state: QuizEditorStoreState) => {
            state.quizDraft.questions.push(getEmptyQuizQuestion());
        },
        deleteSlide: (state: QuizEditorStoreState, action: PayloadAction<number>) => {
            state.quizDraft.questions.splice(action.payload, 1);
            if (action.payload <= state.currentQuestionIndex) {
                const newQuestionIndex = state.currentQuestionIndex - 1;
                state.currentQuestionIndex = newQuestionIndex > 0 ? newQuestionIndex : 0;
            }
        },
        copySlide: (state: QuizEditorStoreState, action: PayloadAction<number>) => {
            const questionIndex = action.payload;
            const questionCopy = state.quizDraft.questions[questionIndex];
            state.quizDraft.questions.splice(action.payload, 0, questionCopy);
        },
        setQuizTitle: (state: QuizEditorStoreState, action: PayloadAction<string>) => {
            state.quizDraft.title = action.payload;
        },
        validate: (state: QuizEditorStoreState) => {
            let isQuizValid = true;

            if (!state.quizDraft.title) {
                isQuizValid = false;
            }

            for (const question of state.quizDraft.questions) {
                question.isValid = true;

                if (!question.title) {
                    question.isValid = false;

                    isQuizValid = false;
                }

                let hasCorrectAnswer = false;

                for (const answer of question.answers) {
                    if (!answer.value) {
                        question.isValid = false;

                        isQuizValid = false;
                    }
                    if (answer.correct) {
                        hasCorrectAnswer = true;
                    }
                }

                if (!hasCorrectAnswer) {
                    question.isValid = false;

                    isQuizValid = false;
                }
            }

            state.quizDraft.isValid = isQuizValid;
        },
        setViewType: (state: QuizEditorStoreState, action: PayloadAction<QuizEditorView>) => {
            state.viewType = action.payload;
        },
        setIsReadOnly: (state: QuizEditorStoreState, action: PayloadAction<boolean>) => {
            state.isReadOnly = action.payload;
        },
        setQuizLoadingStatus: (state: QuizEditorStoreState, action: PayloadAction<LOADING_STATUSES>) => {
            state.quizLoadingStatus = action.payload;
        },
        setQuizId: (state: QuizEditorStoreState, action: PayloadAction<string | undefined>) => {
            state.quizDraft.id = action.payload;
        },
        setIsQuizDraftStarted: (state: QuizEditorStoreState, action: PayloadAction<boolean>) => {
            state.isQuizDraftStarted = action.payload;
        },
    },
});

export const actions = slice.actions;
export const quizEditorReducer = slice.reducer;
